import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Tabs, Form, Input, Select, DatePicker, Button, Table, Modal, Popconfirm, notification, Typography, Spin, Tag, Space, Descriptions } from 'antd';
import { PlusOutlined, SwapOutlined, SaveOutlined, DeleteOutlined, EditOutlined, StopOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { AttachmentsPanel } from '../../components/AttachmentsPanel';
import ProposalEditorModal, { proposalColumns } from '../../components/ProposalEditorModal';
import { LEAD_STATUS_COLORS, LEAD_TYPE_OPTIONS, SOURCE_OPTIONS } from './LeadsPage';

import { CountrySelect } from '../../components/CountrySelect';
const ACTIVITY_TYPES = [
  { value: 'MEETING', label: 'Meeting', color: 'purple' },
  { value: 'CALL', label: 'Call', color: 'blue' },
  { value: 'EMAIL', label: 'E-mail', color: 'cyan' },
  { value: 'NOTE', label: 'Note', color: 'default' },
];
const typeMeta = (v) => ACTIVITY_TYPES.find((t) => t.value === v) || { label: v, color: 'default' };
const toDate = (v) => (v ? moment(v) : null);

/** Meetings / calls / e-mails with the prospective client, newest first. */
function ActivityLogTab({ lead, users, onChanged }) {
  const [rows, setRows] = React.useState(lead.LeadActivities || []);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form] = Form.useForm();

  React.useEffect(() => setRows(lead.LeadActivities || []), [lead]);

  const openCreate = (type) => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ activity_type: type || 'MEETING', activity_at: moment(), user_id: undefined });
    setModalOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    form.setFieldsValue({ ...a, activity_at: toDate(a.activity_at), next_action_date: toDate(a.next_action_date) });
    setModalOpen(true);
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      const payload = {
        ...values,
        activity_at: values.activity_at.toISOString(),
        next_action_date: values.next_action_date ? values.next_action_date.format('YYYY-MM-DD') : null,
      };
      (editing ? request.put(`/leads/${lead.id}/activities/${editing.id}`, payload) : request.post(`/leads/${lead.id}/activities`, payload))
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: editing ? 'Activity updated' : 'Activity logged' });
            setModalOpen(false);
            onChanged();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const handleDelete = (a) => {
    request
      .delete(`/leads/${lead.id}/activities/${a.id}`)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) onChanged();
        else notification.error({ message: r.message });
      });
  };

  return (
    <div>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Text type="secondary">Every meeting, call and e-mail with the prospect, with its outcome and the agreed next step.</Typography.Text>
        <Space>
          {ACTIVITY_TYPES.map((t) => (
            <Button key={t.value} icon={<PlusOutlined />} onClick={() => openCreate(t.value)}>
              {t.label}
            </Button>
          ))}
        </Space>
      </Space>
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={rows}
        locale={{ emptyText: 'Nothing logged yet' }}
        columns={[
          { title: 'When', dataIndex: 'activity_at', width: 140, render: (v) => moment(v).format('YYYY-MM-DD HH:mm') },
          { title: 'Type', dataIndex: 'activity_type', width: 90, render: (v) => <Tag color={typeMeta(v).color}>{typeMeta(v).label}</Tag> },
          {
            title: 'Subject',
            key: 'subject',
            render: (_, a) => (
              <div>
                <strong>{a.subject}</strong>
                {a.summary && <div style={{ whiteSpace: 'pre-wrap', color: '#595959' }}>{a.summary}</div>}
                {a.participants && <Typography.Text type="secondary">With: {a.participants}</Typography.Text>}
              </div>
            ),
          },
          { title: 'Outcome', dataIndex: 'outcome' },
          { title: 'Next action', key: 'next', render: (_, a) => (a.next_action ? `${a.next_action}${a.next_action_date ? ` (by ${a.next_action_date})` : ''}` : '-') },
          { title: 'By', key: 'by', width: 140, render: (_, a) => a.User?.name || '-' },
          {
            title: '',
            key: 'actions',
            width: 80,
            render: (_, a) => (
              <Space size={4}>
                <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(a)} />
                <Popconfirm title="Remove this entry?" onConfirm={() => handleDelete(a)}>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Modal title={editing ? 'Edit activity' : 'Log activity'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSave} width={640}>
        <Form form={form} layout="vertical">
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="activity_type" label="Type" rules={[{ required: true }]} style={{ width: 160 }}>
              <Select options={ACTIVITY_TYPES} />
            </Form.Item>
            <Form.Item name="activity_at" label="Date & time" rules={[{ required: true }]} style={{ width: 220 }}>
              <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="user_id" label="Held by" style={{ width: 200 }} tooltip="Defaults to you">
              <Select allowClear showSearch optionFilterProp="label" options={users.map((u) => ({ value: u.id, label: u.name }))} />
            </Form.Item>
          </Space>
          <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
            <Input maxLength={150} />
          </Form.Item>
          <Form.Item name="participants" label="Participants (their side)">
            <Input maxLength={255} placeholder="e.g. Mr A. Georgiou (director), Ms M. Ioannou (accountant)" />
          </Form.Item>
          <Form.Item name="summary" label="Notes">
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="outcome" label="Outcome">
            <Input maxLength={255} placeholder="e.g. Interested in accounting + VAT; send proposal" />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="next_action" label="Next action" style={{ width: 380 }}>
              <Input maxLength={255} />
            </Form.Item>
            <Form.Item name="next_action_date" label="By" style={{ width: 200 }} tooltip="Also sets the lead's next follow-up date">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}

function LeadProposalsTab({ lead, onChanged }) {
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const rows = lead.ClientProposals || [];
  return (
    <div>
      <div style={{ textAlign: 'right', marginBottom: 12 }}>
        <Button
          icon={<PlusOutlined />}
          disabled={lead.status === 'CONVERTED'}
          onClick={() => {
            setEditing(null);
            setEditorOpen(true);
          }}
        >
          New Proposal
        </Button>
      </div>
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={rows}
        locale={{ emptyText: lead.status === 'CONVERTED' ? 'Proposals moved to the client record on conversion' : 'No proposals yet' }}
        columns={proposalColumns({
          onEdit: (p) => {
            setEditing(p);
            setEditorOpen(true);
          },
          onChange: onChanged,
        })}
      />
      <ProposalEditorModal open={editorOpen} onClose={() => setEditorOpen(false)} onSaved={onChanged} target={{ lead_id: lead.id }} proposal={editing} />
    </div>
  );
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [lead, setLead] = React.useState(null);
  const [users, setUsers] = React.useState([]);
  const [associates, setAssociates] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [lostOpen, setLostOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [form] = Form.useForm();
  const [lostForm] = Form.useForm();
  const status = form.getFieldValue('status');

  const fetchLead = React.useCallback(() => {
    request
      .get(`/leads/${id}`)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          setLead(r.data);
          form.setFieldsValue({ ...r.data, next_follow_up_date: toDate(r.data.next_follow_up_date) });
        } else {
          notification.error({ message: r.message });
        }
      });
  }, [id, form]);

  React.useEffect(() => {
    fetchLead();
    const q = auth.user.companyId ? { company_id: auth.user.companyId } : {};
    request.get('/users', q).then((r) => r.json()).then((r) => r.status && setUsers(r.data));
    request.get('/associates', q).then((r) => r.json()).then((r) => r.status && setAssociates(r.data));
    request.get('/clients', q).then((r) => r.json()).then((r) => r.status && setClients(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = () => {
    form.validateFields().then((values) => {
      setSaving(true);
      request
        .put(`/leads/${id}`, { ...values, company_id: lead.company_id, next_follow_up_date: values.next_follow_up_date ? values.next_follow_up_date.format('YYYY-MM-DD') : null })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Lead saved' });
            fetchLead();
          } else {
            notification.error({ message: r.message });
          }
        })
        .finally(() => setSaving(false));
    });
  };

  const handleConvert = () => {
    request
      .post(`/leads/${id}/convert`, {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: `Converted to client ${r.data.client.client_name}` });
          navigate(`/clients/${r.data.client.id}`);
        } else {
          notification.error({ message: r.message });
        }
      });
  };

  const handleLost = () => {
    lostForm.validateFields().then((values) => {
      const current = form.getFieldsValue();
      request
        .put(`/leads/${id}`, { ...current, company_id: lead.company_id, next_follow_up_date: current.next_follow_up_date ? current.next_follow_up_date.format('YYYY-MM-DD') : null, status: 'LOST', lost_reason: values.lost_reason })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            setLostOpen(false);
            fetchLead();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  if (!lead) return <Spin style={{ marginTop: 40 }} />;
  const converted = lead.status === 'CONVERTED';

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} align="start">
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {lead.lead_name} <Tag color={LEAD_STATUS_COLORS[lead.status]}>{lead.status.replace('_', ' ')}</Tag>
          </Typography.Title>
          <Typography.Text type="secondary">
            <Link to="/leads">Leads</Link> / {LEAD_TYPE_OPTIONS.find((t) => t.value === lead.lead_type)?.label || lead.lead_type}
            {lead.business_name ? ` - trading as ${lead.business_name}` : ''}
            {lead.AssignedUser ? ` - assigned to ${lead.AssignedUser.name}` : ''}
            {converted && lead.ConvertedClient && (
              <span>
                {' '}
                - now client <Link to={`/clients/${lead.ConvertedClient.id}`}>{lead.ConvertedClient.client_name}</Link>
              </span>
            )}
          </Typography.Text>
        </div>
        {!converted && (
          <Space>
            <Button icon={<StopOutlined />} danger disabled={lead.status === 'LOST'} onClick={() => setLostOpen(true)}>
              Mark Lost
            </Button>
            <Popconfirm title="Convert this lead into a Client? Its proposals and profile move across." onConfirm={handleConvert}>
              <Button type="primary" icon={<SwapOutlined />}>
                Convert to Client
              </Button>
            </Popconfirm>
          </Space>
        )}
      </Space>

      <Tabs
        items={[
          {
            key: 'profile',
            label: 'Profile',
            children: (
              <Form form={form} layout="vertical" style={{ maxWidth: 760 }} disabled={converted}>
                <Typography.Title level={5}>Name</Typography.Title>
                <Space style={{ display: 'flex' }} align="start">
                  <Form.Item name="lead_name" label="Registered name" rules={[{ required: true }]} style={{ width: 360 }}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="business_name" label="Business / brand name" style={{ width: 360 }}>
                    <Input maxLength={150} />
                  </Form.Item>
                </Space>
                <Space style={{ display: 'flex' }} align="start">
                  <Form.Item name="lead_type" label="Type" rules={[{ required: true }]} style={{ width: 360 }}>
                    <Select options={LEAD_TYPE_OPTIONS} />
                  </Form.Item>
                  <Form.Item name="language" label="Language" style={{ width: 360 }}>
                    <Select allowClear options={[{ value: 'GREEK', label: 'Greek' }, { value: 'ENGLISH', label: 'English' }]} />
                  </Form.Item>
                </Space>
                <Form.Item name="principal_activities" label="Principal activities">
                  <Input.TextArea rows={2} />
                </Form.Item>

                <Typography.Title level={5}>Contact details</Typography.Title>
                <Space style={{ display: 'flex' }} align="start">
                  <Form.Item name="contact_person" label="Contact person" style={{ width: 360 }}>
                    <Input maxLength={120} />
                  </Form.Item>
                  <Form.Item name="telephone" label="Telephone" style={{ width: 360 }}>
                    <Input maxLength={40} />
                  </Form.Item>
                </Space>
                <Space style={{ display: 'flex' }} align="start">
                  <Form.Item name="email" label="Email" style={{ width: 360 }} rules={[{ type: 'email' }]}>
                    <Input maxLength={120} />
                  </Form.Item>
                  <Form.Item name="website" label="Website" style={{ width: 360 }}>
                    <Input maxLength={150} />
                  </Form.Item>
                </Space>
                <Form.Item name="address_line" label="Address">
                  <Input maxLength={200} />
                </Form.Item>
                <Space style={{ display: 'flex' }} align="start">
                  <Form.Item name="city" label="City" style={{ width: 175 }}>
                    <Input maxLength={80} />
                  </Form.Item>
                  <Form.Item name="district" label="District" style={{ width: 175 }}>
                    <Input maxLength={80} />
                  </Form.Item>
                  <Form.Item name="postal_code" label="Postal code" style={{ width: 175 }}>
                    <Input maxLength={20} />
                  </Form.Item>
                  <Form.Item name="country" label="Country" style={{ width: 200 }}>
                    <CountrySelect />
                  </Form.Item>
                </Space>

                <Typography.Title level={5}>Lead</Typography.Title>
                <Space style={{ display: 'flex' }} align="start">
                  <Form.Item name="source" label="Source" rules={[{ required: true }]} style={{ width: 235 }}>
                    <Select options={SOURCE_OPTIONS} />
                  </Form.Item>
                  <Form.Item name="associate_id" label="Associate (if referred)" style={{ width: 235 }}>
                    <Select allowClear showSearch optionFilterProp="label" options={associates.map((a) => ({ value: a.id, label: a.name }))} />
                  </Form.Item>
                  <Form.Item name="referring_client_id" label="Referred by client" style={{ width: 235 }}>
                    <Select allowClear showSearch optionFilterProp="label" options={clients.map((c) => ({ value: c.id, label: c.client_name }))} />
                  </Form.Item>
                </Space>
                <Space style={{ display: 'flex' }} align="start">
                  <Form.Item name="referring_staff_id" label="Referred by staff" style={{ width: 235 }}>
                    <Select allowClear showSearch optionFilterProp="label" options={users.map((u) => ({ value: u.id, label: u.name }))} />
                  </Form.Item>
                  <Form.Item name="assigned_user_id" label="Assigned to" style={{ width: 235 }}>
                    <Select allowClear showSearch optionFilterProp="label" options={users.map((u) => ({ value: u.id, label: u.name }))} />
                  </Form.Item>
                  <Form.Item name="next_follow_up_date" label="Next follow-up" style={{ width: 235 }}>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Space>
                <Space style={{ display: 'flex' }} align="start">
                  <Form.Item name="status" label="Status" style={{ width: 235 }}>
                    <Select options={['NEW', 'CONTACTED', 'PROPOSAL_SENT', 'LOST'].map((s) => ({ value: s, label: s.replace('_', ' ') }))} disabled={converted} />
                  </Form.Item>
                  <Form.Item name="lost_reason" label="Lost reason" style={{ width: 485 }} hidden={status !== 'LOST' && lead.status !== 'LOST'}>
                    <Input maxLength={255} />
                  </Form.Item>
                </Space>
                <Form.Item name="notes" label="Notes">
                  <Input.TextArea rows={3} />
                </Form.Item>
                {!converted && (
                  <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
                    Save
                  </Button>
                )}
              </Form>
            ),
          },
          {
            key: 'activities',
            label: `Meetings & Communication (${(lead.LeadActivities || []).length})`,
            children: <ActivityLogTab lead={lead} users={users} onChanged={fetchLead} />,
          },
          {
            key: 'proposals',
            label: `Proposals (${(lead.ClientProposals || []).length})`,
            children: <LeadProposalsTab lead={lead} onChanged={fetchLead} />,
          },
          { key: 'files', label: 'Files', children: <AttachmentsPanel ownerType="LEAD" ownerId={+id} categories={['GENERAL', 'CORRESPONDENCE', 'SUPPORTING_DOCUMENT']} /> },
          {
            key: 'summary',
            label: 'Summary',
            children: (
              <Descriptions bordered size="small" column={2} style={{ maxWidth: 900 }}>
                <Descriptions.Item label="Created">{moment(lead.createdAt).format('YYYY-MM-DD')}</Descriptions.Item>
                <Descriptions.Item label="Last update">{moment(lead.updatedAt).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
                <Descriptions.Item label="Referred by">{lead.Associate?.name || lead.ReferringClient?.client_name || lead.ReferringStaff?.name || '-'}</Descriptions.Item>
                <Descriptions.Item label="Next follow-up">{lead.next_follow_up_date || '-'}</Descriptions.Item>
                <Descriptions.Item label="Lost reason" span={2}>{lead.lost_reason || '-'}</Descriptions.Item>
              </Descriptions>
            ),
          },
        ]}
      />

      <Modal title="Mark lead as lost" open={lostOpen} onCancel={() => setLostOpen(false)} onOk={handleLost}>
        <Form form={lostForm} layout="vertical">
          <Form.Item name="lost_reason" label="Why was it lost?" rules={[{ required: true }]}>
            <Input.TextArea rows={3} maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
